import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BellRing, FileWarning } from 'lucide-react';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { OngletFinanceurs } from '../components/financements/OngletFinanceurs';
import { PilotageFinanceurs } from '../components/financements/PilotageFinanceurs';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { useTaillePage } from '../lib/useTaillePage';
import { ErreurApi } from '../lib/api';
import {
  useFinancements,
  type EtatAccord,
  type EtatPriseEnCharge,
  type LigneFinancement,
  type ListeFinancements,
  type TypeFacturation,
} from '../lib/useFinancements';
import { EcheancesDuMois } from '../components/financements/EcheancesDuMois';

/**
 * Un état de la prise en charge ou de l'accord, les lignes qui ont quelque
 * chose à facturer — une échéance (TF-413), un mois d'un contrat de
 * professionnalisation (TF-416), la fin d'un parcours France Travail (TF-418)
 * —, ou celles dont les factures ne pourraient pas encore être émises (TF-406).
 */
type FiltreEtat = EtatPriseEnCharge | EtatAccord | 'A_FACTURER' | 'DONNEES_FACTURE' | '';

const ETATS: { valeur: FiltreEtat; libelle: string }[] = [
  { valeur: '', libelle: 'Tous les états' },
  { valeur: 'A_SAISIR', libelle: 'À saisir' },
  { valeur: 'A_COMPLETER', libelle: 'À compléter' },
  { valeur: 'COMPLETE', libelle: 'Complète' },
  { valeur: 'A_FACTURER', libelle: 'À facturer' },
  { valeur: 'DONNEES_FACTURE', libelle: 'Données de facture à compléter' },
  { valeur: 'SANS_ACCORD', libelle: 'Sans accord (OF)' },
];

/** Apprentissage, contrat de professionnalisation, France Travail (TF-465), ou tous. */
type FiltreType = TypeFacturation | '';

/** Sections de l'écran, et la valeur de `?onglet=` qui les ouvre. */
type OngletFinancements = 'prises' | 'echeances' | 'pilotage' | 'financeurs';

function ongletDe(valeur: string | null): OngletFinancements {
  return valeur === 'echeances' || valeur === 'pilotage' || valeur === 'financeurs' ? valeur : 'prises';
}

function retenue(ligne: LigneFinancement, etat: FiltreEtat, type: FiltreType): boolean {
  if (type && ligne.typeFacturation !== type) return false;
  if (!etat) return true;
  if (etat === 'A_FACTURER') {
    return (
      ligne.aFacturer.echeances > 0 ||
      ligne.aFacturer.mois > 0 ||
      ligne.aFacturer.finParcours > 0 ||
      ligne.aFacturer.premierEquipement > 0 ||
      ligne.aFacturer.avoir
    );
  }
  if (etat === 'DONNEES_FACTURE') return ligne.facture !== null && !ligne.facture.prete;
  return ligne.situation.etat === etat;
}

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Comparaison sans casse ni accent, pour la recherche. */
function cle(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function correspond(ligne: LigneFinancement, recherche: string): boolean {
  const cherche = cle(recherche.trim());
  if (!cherche) return true;
  return [
    `${ligne.apprenant.prenom} ${ligne.apprenant.nom}`,
    `${ligne.apprenant.nom} ${ligne.apprenant.prenom}`,
    ligne.employeur ?? '',
    ligne.formation,
    ligne.payeur ?? '',
    ligne.reference ?? '',
  ].some((valeur) => cle(valeur).includes(cherche));
}

/**
 * Écran « Financeurs & OPCO » (phase 4, TF-404) : le point d'entrée du pôle
 * facturation dans les dossiers. Une ligne par contrat d'apprentissage né —
 * rompu ou terminé compris, la facture de solde en dépend —, avec son payeur,
 * sa référence de prise en charge (DK), son total et ce qui manque pour
 * facturer. Le nom mène au dossier, où le bloc Financement se saisit.
 *
 * Une ligne aussi par dossier OF rattaché à une session ou porteur d'un accord
 * de financement (TF-417) : contrat de professionnalisation ou France Travail,
 * taux horaire × volume d'heures. Un dossier sans accord y paraît « Sans
 * accord », en neutre : tous les parcours OF n'en ont pas.
 *
 * L'état, ce qui manque et les compteurs viennent de l'API
 * (`domain/prise-en-charge.ts`) ; l'écran filtre et pagine ce qu'elle rend. Les
 * contenus de démonstration de la maquette (connexions API des financeurs,
 * aides et subventions) ne sont pas repris : aucun n'existe.
 *
 * Le compteur et le filtre « À facturer » lisent ce que l'alerte « Facture à
 * émettre » annonce au pôle : une échéance arrivée à son dernier jour sans
 * facture émise (TF-413), un mois de contrat de professionnalisation fini avec
 * des heures émargées à facturer (TF-416).
 *
 * Deuxième onglet, « Échéances du mois » (TF-465, `?onglet=echeances`) : les
 * échéances programmées des trois types de facturation, mois par mois, avec
 * leur état et leur montant tels que le bloc Financement les montre
 * (`EcheancesDuMois`).
 *
 * Troisième onglet, « Pilotage par financeur » (TF-428, `?onglet=pilotage`) :
 * pour chaque financeur, ses dossiers, ses références DK, ce qu'il doit et ses
 * retards, filtres et export Excel (`PilotageFinanceurs`).
 *
 * Dernier onglet, « Financeurs » (TF-406) : les fiches des clients des
 * factures, à compléter avant d'émettre. `?financeur=<id>` ouvre une fiche
 * d'emblée — le lien « Compléter la fiche du financeur » du dossier, et celui
 * d'une ligne de la liste.
 *
 * Ce qui empêchera d'émettre se lit sans ouvrir de dossier (TF-406) : le
 * bandeau « Avant d'émettre » dit les mentions de l'émetteur et les fiches des
 * clients à compléter, chaque ligne ce qui lui manque, et le filtre « Données
 * de facture à compléter » les réunit. Tout vient de l'API, la règle même que
 * l'émission appliquera.
 */
export function FinancementsPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const aOuvrir = parametres.get('financeur');
  const [onglet, setOnglet] = useState<OngletFinancements>(() =>
    aOuvrir ? 'financeurs' : ongletDe(parametres.get('onglet')),
  );
  // Un lien vers une fiche, depuis une ligne de la liste, ouvre l'onglet qui la porte.
  useEffect(() => {
    if (aOuvrir) setOnglet('financeurs');
  }, [aOuvrir]);
  const ouvrirFinanceurs = useCallback(() => {
    setOnglet('financeurs');
    setParametres({ onglet: 'financeurs' }, { replace: true });
  }, [setParametres]);
  // Depuis les échéances du mois : la liste des prises en charge, filtrée sur ce qui est à facturer.
  const voirAFacturer = useCallback(() => {
    setOnglet('prises');
    setParametres({ etat: 'A_FACTURER' }, { replace: true });
  }, [setParametres]);
  // La fiche ouverte, le paramètre s'efface : un retour à l'écran ne la rouvre pas.
  const ficheOuverte = useCallback(() => {
    setParametres(
      (avant) => {
        const suivants = new URLSearchParams(avant);
        suivants.delete('financeur');
        suivants.set('onglet', 'financeurs');
        return suivants;
      },
      { replace: true },
    );
  }, [setParametres]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Financeurs & OPCO"
        sousTitre="Pôle facturation · prises en charge des contrats d’apprentissage, accords des parcours OF, échéances du mois, pilotage par financeur et fiches des financeurs"
      />

      <Onglets
        etiquette="Sections de l’écran"
        onglets={[
          { cle: 'prises', libelle: 'Prises en charge' },
          { cle: 'echeances', libelle: 'Échéances du mois' },
          { cle: 'pilotage', libelle: 'Pilotage par financeur' },
          { cle: 'financeurs', libelle: 'Financeurs' },
        ]}
        actif={onglet}
        onChanger={(cle) => {
          setOnglet(cle);
          setParametres(cle === 'prises' ? {} : { onglet: cle }, { replace: true });
        }}
        idPanneau="panneau-financements"
      />

      <PanneauOnglet id="panneau-financements" actif={onglet} className="space-y-6">
        {onglet === 'prises' && <PrisesEnCharge onOuvrirFinanceurs={ouvrirFinanceurs} />}
        {onglet === 'echeances' && <EcheancesDuMois onVoirAFacturer={voirAFacturer} />}
        {onglet === 'pilotage' && <PilotageFinanceurs />}
        {onglet === 'financeurs' && <OngletFinanceurs aOuvrir={aOuvrir} onOuverte={ficheOuverte} />}
      </PanneauOnglet>
    </div>
  );
}

function PrisesEnCharge({ onOuvrirFinanceurs }: { onOuvrirFinanceurs: () => void }): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useFinancements(site);
  const [parametres] = useSearchParams();
  const [recherche, setRecherche] = useState('');
  // `?etat=A_FACTURER` : le bandeau « À facturer » de l'écran « Factures » (TF-422) y mène.
  const [etat, setEtat] = useState<FiltreEtat>(() => {
    const demande = parametres.get('etat');
    return ETATS.some((option) => option.valeur === demande) ? (demande as FiltreEtat) : '';
  });
  const [type, setType] = useState<FiltreType>('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [site, recherche, etat, type, taillePage]);

  const lignes = useMemo(
    () =>
      (data?.lignes ?? []).filter(
        (ligne) => retenue(ligne, etat, type) && correspond(ligne, recherche),
      ),
    [data?.lignes, etat, type, recherche],
  );
  const total = lignes.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const affichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return lignes.slice(debut, debut + taillePage);
  }, [lignes, page, taillePage]);

  const compteurs = data?.compteurs;

  return (
    <>
      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Contrats à financer"
          valeur={compteurs?.contrats ?? '—'}
          detail={[
            site === TOUS_LES_SITES ? 'toutes antennes' : `antenne ${site}`,
            compteurs ? detailEcheanciers(compteurs) : null,
            compteurs ? detailParcoursOf(compteurs) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          ton="navy"
        />
        <Compteur
          libelle="À saisir"
          valeur={compteurs?.aSaisir ?? '—'}
          detail={
            compteurs ? `${compteurs.aCompleter} autre${compteurs.aCompleter > 1 ? 's' : ''} à compléter` : undefined
          }
          ton="red"
        />
        <Compteur
          libelle="Sans référence DK"
          valeur={compteurs?.sansReference ?? '—'}
          detail="à demander au financeur"
          ton="warm"
        />
        <Compteur
          libelle="Total pris en charge"
          valeur={compteurs?.totalPrisEnCharge ?? '—'}
          detail={compteurs ? detailCompletes(compteurs) : undefined}
          ton="mint"
        />
      </div>

      {data && (
        <BandeauAvantEmission
          preparation={data.preparation}
          incompletes={data.compteurs.donneesFactureIncompletes}
          filtreActif={etat === 'DONNEES_FACTURE'}
          onAfficher={() => setEtat('DONNEES_FACTURE')}
          onOuvrirFinanceurs={onOuvrirFinanceurs}
        />
      )}

      {compteurs &&
        (compteurs.echeancesAFacturer > 0 ||
          compteurs.moisAFacturer > 0 ||
          compteurs.finsDeParcoursAFacturer > 0 ||
          compteurs.premiersEquipementsAFacturer > 0 ||
          compteurs.avoirsAEtablir > 0) && (
          <BandeauAFacturer
            echeances={compteurs.echeancesAFacturer}
            contrats={compteurs.contratsAFacturer}
            mois={compteurs.moisAFacturer}
            finsDeParcours={compteurs.finsDeParcoursAFacturer}
            parcours={compteurs.parcoursAFacturer}
            premiersEquipements={compteurs.premiersEquipementsAFacturer}
            avoirs={compteurs.avoirsAEtablir}
            filtreActif={etat === 'A_FACTURER'}
            onAfficher={() => setEtat('A_FACTURER')}
          />
        )}

      <Carte
        titre="Prises en charge"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher une prise en charge"
              placeholder="Apprenant, employeur, référence"
            />
            <ChampFiltre
              valeur={type}
              onChange={(valeur) => setType(valeur as FiltreType)}
              etiquette="Filtrer par type de facturation"
            >
              <option value="">Toutes les facturations</option>
              {data?.filtres.typesFacturation.map((option) => (
                <option key={option.valeur} value={option.valeur}>
                  {option.libelle}
                </option>
              ))}
            </ChampFiltre>
            <ChampFiltre
              valeur={etat}
              onChange={(valeur) => setEtat(valeur as FiltreEtat)}
              etiquette="Filtrer par état"
            >
              {ETATS.map((option) => (
                <option key={option.valeur} value={option.valeur}>
                  {option.libelle}
                </option>
              ))}
            </ChampFiltre>
          </div>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi ? error.message : 'Les prises en charge n’ont pas pu être chargées.'
              }
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement des prises en charge…" />}

        {!isLoading && !error && total === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            {data && data.lignes.length === 0
              ? 'Aucun contrat d’apprentissage validé ni parcours OF dans ce périmètre.'
              : 'Aucune prise en charge ne correspond aux filtres actifs.'}
          </p>
        )}

        {/* Sous la tablette, une carte par contrat : le tableau y défilerait de côté. */}
        {!isLoading && !error && total > 0 && (
          <ul className="divide-y divide-gray-100 md:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.cle} className="space-y-2 px-4 py-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <LienDossier ligne={ligne} />
                    <div className="text-xs text-codialis-gray">{ligne.formation}</div>
                  </div>
                  <Pastille texte={ligne.situation.libelle} ton={ligne.situation.ton} />
                </div>
                <Contrat ligne={ligne} />
                <Financement ligne={ligne} />
                {ligne.situation.detail && (
                  <p className="text-[11px] font-semibold text-amber-700">{ligne.situation.detail}</p>
                )}
                <DonneesFacture ligne={ligne} />
                <EtatEcheancier ligne={ligne} />
                <Avancement ligne={ligne} />
                <AFacturer ligne={ligne} />
                <Link
                  to={`/adm-dossier/${ligne.apprenant.id}`}
                  className="inline-flex min-h-[44px] items-center text-xs font-semibold text-codialis-sky hover:underline"
                >
                  Ouvrir le dossier
                </Link>
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !error && total > 0 && (
          <div className="relative hidden overflow-x-auto md:block">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Apprenant', 'Contrat ou accord', 'Payeur · référence', 'Total', 'État', ''].map(
                    (colonne, index) => (
                      <th
                        key={colonne || index}
                        className={`px-3 py-3 text-[11px] font-bold uppercase tracking-wider text-codialis-gray lg:px-4 ${
                          colonne === 'Total' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {colonne || <span className="sr-only">Actions</span>}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.cle} className="align-top transition hover:bg-gray-50">
                    <td className="px-3 py-3 lg:px-4">
                      <LienDossier ligne={ligne} />
                      <div className="text-xs text-codialis-gray">{ligne.formation}</div>
                      <div className="mt-1.5">
                        <PastilleSite site={ligne.siteCode} />
                      </div>
                    </td>
                    <td className="px-3 py-3 lg:px-4">
                      <Contrat ligne={ligne} />
                    </td>
                    <td className="px-3 py-3 lg:px-4">
                      <Financement ligne={ligne} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-codialis-navy lg:px-4">
                      {ligne.total.texte ?? '—'}
                    </td>
                    <td className="max-w-[220px] space-y-1 px-3 py-3 lg:px-4">
                      <Pastille texte={ligne.situation.libelle} ton={ligne.situation.ton} />
                      {ligne.situation.detail && (
                        <p className="text-[11px] text-amber-700">{ligne.situation.detail}</p>
                      )}
                      <DonneesFacture ligne={ligne} />
                      <EtatEcheancier ligne={ligne} />
                      <Avancement ligne={ligne} />
                      <AFacturer ligne={ligne} />
                    </td>
                    <td className="px-3 py-3 text-right lg:px-4">
                      <Link
                        to={`/adm-dossier/${ligne.apprenant.id}`}
                        className="whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline"
                      >
                        Ouvrir le dossier
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="financement"
          />
        )}
      </Carte>
    </>
  );
}

/** « 3 prises en charge complètes », accords des parcours OF compris. */
function detailCompletes(compteurs: ListeFinancements['compteurs']): string {
  const completes = compteurs.completes + compteurs.parcoursOf.complets;
  return `${completes} prise${completes > 1 ? 's' : ''} en charge complète${completes > 1 ? 's' : ''}`;
}

/** « 2 parcours OF, 1 sans accord » ; `null` sans parcours OF. */
function detailParcoursOf(compteurs: ListeFinancements['compteurs']): string | null {
  const { dossiers, sansAccord } = compteurs.parcoursOf;
  if (dossiers === 0) return null;
  return `${dossiers} parcours OF${sansAccord > 0 ? `, ${sansAccord} sans accord` : ''}`;
}

/** « 3 échéanciers à générer, 1 à revoir » ; `null` quand tous sont paramétrés. */
function detailEcheanciers(compteurs: ListeFinancements['compteurs']): string | null {
  const { echeanciersAGenerer: aGenerer, echeanciersARevoir: aRevoir } = compteurs;
  const parties = [
    aGenerer > 0 ? `${aGenerer} échéancier${aGenerer > 1 ? 's' : ''} à générer` : null,
    aRevoir > 0 ? `${aRevoir} à revoir` : null,
  ].filter(Boolean);
  return parties.length > 0 ? parties.join(', ') : null;
}

function LienDossier({ ligne }: { ligne: LigneFinancement }): JSX.Element {
  return (
    <Link
      to={`/adm-dossier/${ligne.apprenant.id}`}
      className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
    >
      {ligne.apprenant.prenom} {ligne.apprenant.nom}
    </Link>
  );
}

/**
 * Le contrat d'apprentissage, ou l'accord d'un parcours OF : son employeur
 * (un parcours France Travail n'en a pas), ses dates et sa nature.
 */
function Contrat({ ligne }: { ligne: LigneFinancement }): JSX.Element {
  const of = ligne.nature === 'ACCORD_OF';
  return (
    <div className="min-w-0 space-y-1">
      {(ligne.employeur || !of) && (
        <div className="break-words text-sm text-codialis-navy">{ligne.employeur}</div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-codialis-gray">
        {ligne.dateDebut && ligne.dateFin ? (
          <span>
            {jourFrancais(ligne.dateDebut)} → {jourFrancais(ligne.dateFin)}
          </span>
        ) : (
          of && <span>Dates à préciser</span>
        )}
        <Pastille texte={ligne.situationContrat.libelle} ton={ligne.situationContrat.ton} />
      </div>
    </div>
  );
}

/**
 * « Échéancier : à générer », « Échéancier : 8 échéances, tous les 3 mois »
 * (TF-409) ; en OF, comment l'accord se facture.
 */
function EtatEcheancier({ ligne }: { ligne: LigneFinancement }): JSX.Element | null {
  const { echeancier } = ligne;
  if (!echeancier) {
    return ligne.accord?.facturation ? (
      <p className="text-[11px] text-codialis-gray">{ligne.accord.facturation}</p>
    ) : null;
  }
  const ton =
    echeancier.etat === 'PARAMETRE'
      ? 'text-codialis-gray'
      : echeancier.etat === 'A_REVOIR'
        ? 'font-semibold text-red-700'
        : 'font-semibold text-amber-700';
  return (
    <p className={`text-[11px] ${ton}`}>
      Échéancier : {echeancier.etat === 'PARAMETRE' ? echeancier.detail : echeancier.libelle.toLowerCase()}
    </p>
  );
}

/**
 * Ce que l'alerte « Facture à émettre » annonce au pôle, sur tout le
 * périmètre : les échéances arrivées à leur dernier jour sans facture émise
 * (TF-413), les mois de contrats de professionnalisation finis avec des heures
 * à facturer (TF-416).
 */
function BandeauAFacturer({
  echeances,
  contrats,
  mois,
  finsDeParcours,
  parcours,
  premiersEquipements,
  avoirs,
  filtreActif,
  onAfficher,
}: {
  echeances: number;
  contrats: number;
  mois: number;
  finsDeParcours: number;
  /** Parcours OF qui ont un mois ou leur fin de parcours à facturer : un parcours n'a que l'un des deux. */
  parcours: number;
  premiersEquipements: number;
  avoirs: number;
  filtreActif: boolean;
  onAfficher: () => void;
}): JSX.Element {
  const mensuels = parcours - finsDeParcours;
  const titres = [
    echeances > 0
      ? `${echeances} échéance${echeances > 1 ? 's' : ''} à facturer, sur ${contrats} contrat${contrats > 1 ? 's' : ''}`
      : null,
    mois > 0 ? `${mois} mois d’heures à facturer, sur ${mensuels} parcours OF` : null,
    finsDeParcours > 0
      ? `${finsDeParcours} fin${finsDeParcours > 1 ? 's' : ''} de parcours France Travail à facturer`
      : null,
    premiersEquipements > 0
      ? `${premiersEquipements} contrat${premiersEquipements > 1 ? 's' : ''} aux frais de 1er équipement à facturer`
      : null,
    avoirs > 0 ? `${avoirs} contrat${avoirs > 1 ? 's' : ''} trop facturé${avoirs > 1 ? 's' : ''} : avoir partiel à établir` : null,
  ].filter((titre): titre is string => titre !== null);
  return (
    <div className="flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
        <BellRing className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        {titres.map((titre) => (
          <div key={titre} className="text-sm font-semibold text-amber-900">
            {titre}
          </div>
        ))}
        <p className="text-xs text-amber-800">
          Arrivés à leur dernier jour sans facture émise : le pôle facturation en est prévenu par le module
          d’alertes.
        </p>
      </div>
      {!filtreActif && (
        <BoutonSecondaire onClick={onAfficher}>
          {echeances + premiersEquipements + avoirs > 0 ? 'Afficher ces contrats' : 'Afficher ces parcours'}
        </BoutonSecondaire>
      )}
    </div>
  );
}

/**
 * Ce que la ligne doit encore dire pour que ses factures s'émettent, au-delà
 * de l'état du bloc (TF-406) : la fiche du client — un financeur se complète
 * dans l'onglet « Financeurs », une entreprise au répertoire —, les dates de
 * formation.
 */
function DonneesFacture({ ligne }: { ligne: LigneFinancement }): JSX.Element | null {
  const { facture } = ligne;
  if (!facture || (!facture.ficheClient && !facture.detailDossier)) return null;
  const { ficheClient } = facture;
  return (
    <div className="space-y-0.5 text-[11px] font-semibold text-amber-700">
      {ficheClient && (
        <p className="break-words">
          {ficheClient.detail}{' '}
          {ficheClient.nature === 'FINANCEUR' ? (
            <Link
              to={`/financing?financeur=${encodeURIComponent(ficheClient.id)}`}
              className="inline-flex items-center text-codialis-sky underline-offset-2 hover:underline [@media(pointer:coarse)]:min-h-[40px]"
            >
              Compléter la fiche
            </Link>
          ) : (
            <span className="font-normal text-codialis-gray">À compléter au répertoire.</span>
          )}
        </p>
      )}
      {facture.detailDossier && <p>{facture.detailDossier}</p>}
    </div>
  );
}

/**
 * Ce qui empêchera d'émettre, au-delà d'une ligne (TF-406) : les mentions de
 * l'émetteur, sans lesquelles aucune facture ne s'émet, et les fiches des
 * clients à compléter une fois pour tous leurs dossiers ; puis le nombre de
 * lignes dont les factures ne pourraient pas encore être émises, et le filtre
 * qui les réunit. Rien à dire : rien n'est affiché.
 */
function BandeauAvantEmission({
  preparation,
  incompletes,
  filtreActif,
  onAfficher,
  onOuvrirFinanceurs,
}: {
  preparation: ListeFinancements['preparation'];
  incompletes: number;
  filtreActif: boolean;
  onAfficher: () => void;
  onOuvrirFinanceurs: () => void;
}): JSX.Element | null {
  const { emetteur, fichesClients } = preparation;
  if (emetteur.complet && fichesClients.length === 0 && incompletes === 0) return null;
  const financeurs = fichesClients.filter((fiche) => fiche.nature === 'FINANCEUR');
  const entreprises = fichesClients.filter((fiche) => fiche.nature === 'ENTREPRISE');
  return (
    <section
      aria-label="Avant d’émettre"
      className="flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:px-5"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
        <FileWarning className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="text-sm font-semibold text-amber-900">Avant d’émettre</div>
        {!emetteur.complet && (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="min-w-0 text-xs text-amber-800">
              <span className="font-semibold">Mentions de l’émetteur non réglées</span> ({emetteur.manques.join(', ')}) :
              aucune facture ne peut être émise.
            </p>
            <Link
              to="/fac-parametres"
              className="inline-flex min-h-[40px] shrink-0 items-center rounded-xl border border-amber-300 bg-white px-3 text-xs font-semibold text-amber-900 transition hover:bg-amber-100"
            >
              Paramètres de facturation
            </Link>
          </div>
        )}
        {financeurs.length > 0 && (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="min-w-0 break-words text-xs text-amber-800">
              <span className="font-semibold">
                {financeurs.length === 1
                  ? '1 fiche de financeur à compléter'
                  : `${financeurs.length} fiches de financeurs à compléter`}
              </span>{' '}
              : {financeurs.map((fiche) => `${fiche.nom} (${dossiers(fiche.dossiers)})`).join(', ')}.
            </p>
            <div className="shrink-0">
              <BoutonSecondaire onClick={onOuvrirFinanceurs}>Compléter les fiches</BoutonSecondaire>
            </div>
          </div>
        )}
        {entreprises.length > 0 && (
          <p className="break-words text-xs text-amber-800">
            <span className="font-semibold">
              {entreprises.length === 1
                ? '1 entreprise qui paie elle-même'
                : `${entreprises.length} entreprises qui paient elles-mêmes`}
            </span>{' '}
            : {entreprises.map((fiche) => `${fiche.nom} (${fiche.manques.join(', ')})`).join(', ')} — leur fiche se
            complète au répertoire, par la relation entreprise ou l’administratif.
          </p>
        )}
        {incompletes > 0 && (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="min-w-0 text-xs text-amber-800">
              <span className="font-semibold">
                {incompletes === 1
                  ? '1 contrat ou accord ne peut pas encore être facturé'
                  : `${incompletes} contrats ou accords ne peuvent pas encore être facturés`}
              </span>{' '}
              : payeur, référence DK, fiche du client ou dates de formation.
            </p>
            {!filtreActif && (
              <div className="shrink-0">
                <BoutonSecondaire onClick={onAfficher}>Afficher ces lignes</BoutonSecondaire>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function dossiers(nombre: number): string {
  return `${nombre} dossier${nombre > 1 ? 's' : ''}`;
}

/**
 * « Échéance n° 2 à facturer », « Solde à facturer (échéance n° 4) »,
 * « Octobre 2026 à facturer », « Fin de parcours à facturer » : la lecture de
 * l'alerte « Facture à émettre ». Un trop-facturé, qui demande un avoir
 * partiel et non une facture, se lit en rouge (TF-415).
 */
function AFacturer({ ligne }: { ligne: LigneFinancement }): JSX.Element | null {
  if (!ligne.aFacturer.texte) return null;
  return (
    <p className={`text-[11px] font-semibold ${ligne.aFacturer.avoir ? 'text-red-700' : 'text-amber-700'}`}>
      {ligne.aFacturer.texte}
    </p>
  );
}

/** « Facturé 3 696,00 € sur 7 392,00 € · solde le 31/08/2027 » : où en est le contrat (TF-415). */
function Avancement({ ligne }: { ligne: LigneFinancement }): JSX.Element | null {
  if (!ligne.avancement?.resume) return null;
  return <p className="break-words text-[11px] tabular-nums text-codialis-gray">{ligne.avancement.resume}</p>;
}

function Financement({ ligne }: { ligne: LigneFinancement }): JSX.Element {
  // Un dossier OF sans accord n'a ni payeur ni référence à attendre.
  if (ligne.situation.etat === 'SANS_ACCORD') {
    return <div className="text-sm text-codialis-gray">Aucun accord saisi</div>;
  }
  return (
    <div className="min-w-0 space-y-0.5 text-sm">
      <div className="break-words text-codialis-navy">{ligne.payeur ?? 'Payeur non renseigné'}</div>
      <div className="break-all font-mono text-[11px] text-codialis-gray">
        {ligne.reference ?? 'Sans référence'}
      </div>
      {ligne.accord?.calcul && (
        <div className="text-[11px] tabular-nums text-codialis-gray">{ligne.accord.calcul}</div>
      )}
      <div className="text-[11px] font-semibold tabular-nums text-codialis-navy md:hidden">
        {ligne.total.texte ? `Total : ${ligne.total.texte}` : 'Total inconnu'}
      </div>
    </div>
  );
}
